import { Bus } from "./Bus"
import { u16, u8 } from "./utils"

enum Flags {
    Carry,
    Zero,
    DisableInterrupts,
    DecimalMode,
    Break,
    Unused,
    Overflow,
    Negative,
}

export class Cpu6502 {
    accumulator = 0x00
    xRegister = 0x00
    yRegister = 0x00
    stackPointer = 0x00
    programCounter = 0x0000
    statusRegister = 0x00

    private fetched = 0x00
    private temp = 0x0000
    private addressAbs = 0x0000
    private addressRel = 0x00
    private opcode = 0x00
    private cycles = 0
    private clockCount = 0

    private lookup

    private bus!: Bus

    constructor() {
        this.lookup = this.getLookup()
    }

    connectBus(bus: Bus) {
        this.bus = bus
    }

    private read(address: number) {
        return this.bus.cpuRead(address)
    }

    private write(address: number, data: number) {
        this.bus.cpuWrite(address, data)
    }

    fetch() {
        if (this.lookup[this.opcode]!.addressMode !== this.IMP) {
            this.fetched = this.read(this.addressAbs)
        }

        return this.fetched
    }

    private getFlag(flag: Flags) {
        return (this.statusRegister & (1 << flag)) > 0 ? 1 : 0
    }

    private setFlag(flag: Flags, value: boolean | number) {
        if (value) this.statusRegister |= 1 << flag
        else this.statusRegister &= ~(1 << flag)
    }

    reset() {
        this.addressAbs = 0xfffc

        const low = this.read(this.addressAbs)
        const high = this.read(u16(this.addressAbs + 1))

        this.programCounter = (high << 8) | low

        this.accumulator = 0
        this.xRegister = 0
        this.yRegister = 0
        this.stackPointer = 0xfd
        this.statusRegister = 0x00 | this.getFlag(Flags.Unused)

        this.addressRel = 0x0000
        this.addressAbs = 0x0000
        this.fetched = 0x00

        this.cycles = 8
    }

    interruptRequest() {
        if (this.getFlag(Flags.DisableInterrupts)) return

        this.write(u16(0x0100 + this.stackPointer), (this.programCounter >> 8) & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)
        this.write(u16(0x0100 + this.stackPointer), this.programCounter & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)

        this.setFlag(Flags.Break, false)
        this.setFlag(Flags.Unused, true)
        this.setFlag(Flags.DisableInterrupts, true)
        this.write(u16(0x0100 + this.stackPointer), this.statusRegister)
        this.stackPointer = u8(this.stackPointer - 1)

        this.addressAbs = 0xfffe
        const low = this.read(this.addressAbs)
        const high = this.read(u16(this.addressAbs + 1))
        this.programCounter = (high << 8) | low

        this.cycles = 7
    }

    nonMaskableInterruptRequest() {
        this.write(u16(0x0100 + this.stackPointer), (this.programCounter >> 8) & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)
        this.write(u16(0x0100 + this.stackPointer), this.programCounter & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)

        this.setFlag(Flags.Break, false)
        this.setFlag(Flags.Unused, true)
        this.setFlag(Flags.DisableInterrupts, true)
        this.write(u16(0x0100 + this.stackPointer), this.statusRegister)
        this.stackPointer = u8(this.stackPointer - 1)

        this.addressAbs = 0xfffa
        const low = this.read(this.addressAbs)
        const high = this.read(u16(this.addressAbs + 1))
        this.programCounter = (high << 8) | low

        this.cycles = 8
    }

    clock() {
        if (this.cycles === 0) {
            this.opcode = this.read(this.programCounter)

            this.setFlag(Flags.Unused, true)

            this.programCounter = u16(this.programCounter + 1)

            const instruction = this.lookup[this.opcode]!

            this.cycles = instruction.cycles

            const additionalAddressModeCycles = instruction.addressMode()
            const additionalOperationCycles = instruction.operate()

            this.cycles += additionalAddressModeCycles & additionalOperationCycles
        }

        this.clockCount++

        this.cycles--
    }

    // Adress modes
    IMP = () => {
        this.fetched = this.accumulator
        return 0
    }

    IMM = () => {
        this.addressAbs = this.programCounter
        this.programCounter = u16(this.programCounter + 1)

        return 0
    }

    ZP0 = () => {
        this.addressAbs = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)
        this.addressAbs &= 0x00ff

        return 0
    }

    ZPX = () => {
        this.addressAbs = u16(this.read(this.programCounter) + this.xRegister)
        this.programCounter = u16(this.programCounter + 1)
        this.addressAbs &= 0x00ff

        return 0
    }

    ZPY = () => {
        this.addressAbs = u16(this.read(this.programCounter) + this.yRegister)
        this.programCounter = u16(this.programCounter + 1)
        this.addressAbs &= 0x00ff

        return 0
    }

    REL = () => {
        this.addressRel = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        if (this.addressRel & 0x80) this.addressRel |= 0xff00

        return 0
    }

    ABS = () => {
        const low = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const high = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        this.addressAbs = (high << 8) | low

        return 0
    }

    ABX = () => {
        const low = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const high = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        this.addressAbs = (high << 8) | low
        this.addressAbs = u16(this.addressAbs + this.xRegister)

        if ((this.addressAbs & 0xff00) !== high << 8) return 1
        else return 0
    }

    ABY = () => {
        const low = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const high = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        this.addressAbs = (high << 8) | low
        this.addressAbs = u16(this.addressAbs + this.yRegister)

        if ((this.addressAbs & 0xff00) !== high << 8) return 1
        else return 0
    }

    IND = () => {
        const pointerLow = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const pointerHigh = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const pointer = (pointerHigh << 8) | pointerLow

        if (pointerLow === 0x00ff) this.addressAbs = (this.read(pointer & 0xff00) << 8) | this.read(pointer)
        else this.addressAbs = (this.read(u16(pointer + 1)) << 8) | this.read(pointer)

        return 0
    }

    IZX = () => {
        const temp = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const low = this.read(u16(temp + this.xRegister) & 0x00ff)
        const high = this.read(u16(temp + this.xRegister + 1) & 0x00ff)

        this.addressAbs = (high << 8) | low

        return 0
    }

    IZY = () => {
        const temp = this.read(this.programCounter)
        this.programCounter = u16(this.programCounter + 1)

        const low = this.read(temp & 0x00ff)
        const high = this.read(u16(temp + 1) & 0x00ff)

        this.addressAbs = (high << 8) | low
        this.addressAbs = u16(this.addressAbs + this.yRegister)

        if ((this.addressAbs & 0xff00) !== high << 8) return 1
        else return 0
    }

    // Instructions
    BRK = () => {
        this.programCounter = u16(this.programCounter + 1)

        this.setFlag(Flags.DisableInterrupts, true)

        this.write(u16(0x0100 + this.stackPointer), (this.programCounter >> 8) & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)

        this.write(u16(0x0100 + this.stackPointer), this.programCounter & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)

        this.setFlag(Flags.Break, 1)

        this.write(u16(0x0100 + this.stackPointer), this.statusRegister)
        this.stackPointer = u8(this.stackPointer - 1)

        this.setFlag(Flags.Break, 0)

        this.programCounter = this.read(0xfffe) | (this.read(0xffff) << 8)
        return 0
    }

    ORA = () => {
        this.fetch()
        this.accumulator = this.accumulator | this.fetched
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 1
    }

    XXX = () => {
        return 0
    }

    NOP = () => {
        switch (this.opcode) {
            case 0x1c:
            case 0x3c:
            case 0x5c:
            case 0x7c:
            case 0xdc:
            case 0xfc:
                return 1
        }

        return 0
    }

    ASL = () => {
        this.fetch()
        this.temp = this.fetched << 1

        this.setFlag(Flags.Carry, (this.temp & 0xff00) > 0)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.temp & 0x80)

        // Address mode: IMP
        if (this.opcode === 0x0a) this.accumulator = this.temp & 0x00ff
        else this.write(this.addressAbs, this.temp & 0x00ff)

        return 0
    }

    PHP = () => {
        this.write(
            u16(0x0100 + this.stackPointer),
            this.statusRegister | (1 << Flags.Break) | (1 << Flags.Unused),
        )

        this.setFlag(Flags.Break, false)
        this.setFlag(Flags.Unused, false)

        this.stackPointer = u8(this.stackPointer - 1)
        return 0
    }

    BPL = () => {
        if (this.getFlag(Flags.Negative)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs

        return 0
    }

    CLC = () => {
        this.setFlag(Flags.Carry, false)
        return 0
    }

    JSR = () => {
        this.programCounter = u16(this.programCounter - 1)

        this.write(u16(0x0100 + this.stackPointer), (this.programCounter >> 8) & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)
        this.write(u16(0x0100 + this.stackPointer), this.programCounter & 0x00ff)
        this.stackPointer = u8(this.stackPointer - 1)

        this.programCounter = this.addressAbs
        return 0
    }

    AND = () => {
        this.fetch()
        this.accumulator = this.accumulator & this.fetched
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 1
    }

    BIT = () => {
        this.fetch()
        this.temp = this.accumulator & this.fetched
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.fetched & (1 << 7))
        this.setFlag(Flags.Overflow, this.fetched & (1 << 6))
        return 0
    }

    ROL = () => {
        this.fetch()
        this.temp = (this.fetched << 1) | this.getFlag(Flags.Carry)

        this.setFlag(Flags.Carry, this.temp & 0xff00)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x0000)
        this.setFlag(Flags.Negative, this.temp & 0x0080)

        // Address mode: IMP
        if (this.opcode === 0x2a) this.accumulator = this.temp & 0x00ff
        else this.write(this.addressAbs, this.temp & 0x00ff)
        return 0
    }

    PLP = () => {
        this.stackPointer = u8(this.stackPointer + 1)
        this.statusRegister = this.read(u16(0x0100 + this.stackPointer))
        this.setFlag(Flags.Unused, true)
        return 0
    }

    BMI = () => {
        if (!this.getFlag(Flags.Negative)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs
        return 0
    }

    SEC = () => {
        this.setFlag(Flags.Carry, true)
        return 0
    }

    RTI = () => {
        this.stackPointer = u8(this.stackPointer + 1)
        this.statusRegister = this.read(u16(0x0100 + this.stackPointer))
        this.setFlag(Flags.Break, false)
        this.setFlag(Flags.Unused, false)

        this.stackPointer = u8(this.stackPointer + 1)
        const pcLow = this.read(u16(0x0100 + this.stackPointer))
        this.stackPointer = u8(this.stackPointer + 1)
        const pcHigh = this.read(u16(0x0100 + this.stackPointer))
        this.programCounter = (pcHigh << 8) | pcLow // Construct properly!

        return 0
    }

    EOR = () => {
        this.fetch()
        this.accumulator = this.accumulator ^ this.fetched
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 1
    }

    LSR = () => {
        this.fetch()
        this.setFlag(Flags.Carry, this.fetched & 0x0001)
        this.temp = this.fetched >> 1
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x0000)
        this.setFlag(Flags.Negative, this.temp & 0x0080)

        // Addres mode: IMP
        if (this.opcode === 0x4a) this.accumulator = this.temp & 0x00ff
        else this.write(this.addressAbs, this.temp & 0x00ff)
        return 0
    }

    PHA = () => {
        this.write(u16(0x0100 + this.stackPointer), this.accumulator)
        this.stackPointer = u8(this.stackPointer - 1)
        return 0
    }

    JMP = () => {
        this.programCounter = this.addressAbs
        return 0
    }

    BVC = () => {
        if (this.getFlag(Flags.Overflow)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs

        return 0
    }

    CLI = () => {
        this.setFlag(Flags.DisableInterrupts, false)
        return 0
    }

    RTS = () => {
        this.stackPointer = u8(this.stackPointer + 1)
        this.programCounter = this.read(u16(0x0100 + this.stackPointer))
        this.stackPointer = u8(this.stackPointer + 1)
        this.programCounter |= this.read(u16(0x0100 + this.stackPointer)) << 8

        this.programCounter = u16(this.programCounter + 1)
        return 0
    }

    ADC = () => {
        this.fetch()

        this.temp = u16(this.accumulator + this.fetched + this.getFlag(Flags.Carry))

        this.setFlag(Flags.Carry, this.temp > 0xff)

        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0)

        this.setFlag(
            Flags.Overflow,
            ~(this.accumulator ^ this.fetched) & (this.accumulator ^ this.temp) & 0x0080,
        )

        this.setFlag(Flags.Negative, this.temp & 0x80)

        this.accumulator = this.temp & 0x00ff

        return 1
    }

    ROR = () => {
        this.fetch()
        this.temp = (this.getFlag(Flags.Carry) << 7) | (this.fetched >> 1)
        this.setFlag(Flags.Carry, this.fetched & 0x01)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.temp & 0x0080)

        if (this.opcode === 0x6a) this.accumulator = this.temp & 0x00ff
        else this.write(this.addressAbs, this.temp & 0x00ff)
        return 0
    }

    PLA = () => {
        this.stackPointer = u8(this.stackPointer + 1)
        this.accumulator = this.read(u16(0x0100 + this.stackPointer))
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 0
    }

    BVS = () => {
        if (!this.getFlag(Flags.Overflow)) return 0
        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs
        return 0
    }

    SEI = () => {
        this.setFlag(Flags.DisableInterrupts, true)
        return 0
    }

    STA = () => {
        this.write(this.addressAbs, this.accumulator)
        return 0
    }

    STY = () => {
        this.write(this.addressAbs, this.yRegister)
        return 0
    }

    STX = () => {
        this.write(this.addressAbs, this.xRegister)
        return 0
    }

    DEY = () => {
        this.yRegister = u8(this.yRegister - 1)
        this.setFlag(Flags.Zero, this.yRegister === 0x00)
        this.setFlag(Flags.Negative, this.yRegister & 0x80)
        return 0
    }

    TXA = () => {
        this.accumulator = this.xRegister
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 0
    }

    BCC = () => {
        if (this.getFlag(Flags.Carry)) return 0
        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs
        return 0
    }

    TYA = () => {
        this.accumulator = this.yRegister
        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 0
    }

    TXS = () => {
        this.stackPointer = this.xRegister
        return 0
    }

    LDY = () => {
        this.fetch()
        this.yRegister = this.fetched
        this.setFlag(Flags.Zero, this.yRegister === 0x00)
        this.setFlag(Flags.Negative, this.yRegister & 0x80)
        return 1
    }

    LDA = () => {
        this.fetch()
        this.accumulator = this.fetched

        this.setFlag(Flags.Zero, this.accumulator === 0x00)
        this.setFlag(Flags.Negative, this.accumulator & 0x80)
        return 1
    }

    LDX = () => {
        this.fetch()
        this.xRegister = this.fetched
        this.setFlag(Flags.Zero, this.xRegister === 0x00)
        this.setFlag(Flags.Negative, this.xRegister & 0x80)
        return 1
    }

    TAY = () => {
        this.yRegister = this.accumulator
        this.setFlag(Flags.Zero, this.yRegister === 0x00)
        this.setFlag(Flags.Negative, this.yRegister & 0x80)
        return 0
    }

    TAX = () => {
        this.xRegister = this.accumulator
        this.setFlag(Flags.Zero, this.xRegister === 0x00)
        this.setFlag(Flags.Negative, this.xRegister & 0x80)
        return 0
    }

    BCS = () => {
        if (!this.getFlag(Flags.Carry)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs
        return 0
    }

    CLV = () => {
        this.setFlag(Flags.Overflow, false)
        return 0
    }

    TSX = () => {
        this.xRegister = this.stackPointer
        this.setFlag(Flags.Zero, this.xRegister === 0x00)
        this.setFlag(Flags.Negative, this.xRegister & 0x80)
        return 0
    }

    CPY = () => {
        this.fetch()
        this.temp = this.yRegister - this.fetched

        this.setFlag(Flags.Carry, this.yRegister >= this.fetched)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        return 0
    }

    CMP = () => {
        this.fetch()
        this.temp = u16(this.accumulator - this.fetched)

        this.setFlag(Flags.Carry, this.accumulator >= this.fetched)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        return 1
    }

    DEC = () => {
        this.fetch()
        this.temp = u16(this.fetched - 1)
        this.write(this.addressAbs, this.temp & 0x00ff)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x0000)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        return 0
    }

    INY = () => {
        this.yRegister = u8(this.yRegister + 1)
        this.setFlag(Flags.Zero, this.yRegister === 0x00)
        this.setFlag(Flags.Negative, this.yRegister & 0x80)
        return 0
    }

    DEX = () => {
        this.xRegister = u8(this.xRegister - 1)
        this.setFlag(Flags.Zero, this.xRegister === 0x00)
        this.setFlag(Flags.Negative, this.xRegister & 0x80)
        return 0
    }

    BNE = () => {
        if (this.getFlag(Flags.Zero)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) {
            this.cycles++
        }

        this.programCounter = this.addressAbs
        return 0
    }

    CLD = () => {
        this.setFlag(Flags.DecimalMode, false)
        return 0
    }

    CPX = () => {
        this.fetch()
        this.temp = u16(this.xRegister - this.fetched)
        this.setFlag(Flags.Carry, this.xRegister >= this.fetched)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x00)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        return 0
    }

    SBC = () => {
        this.fetch()

        const value = this.fetched ^ 0x00ff

        this.temp = u16(this.accumulator + value + this.getFlag(Flags.Carry))
        this.setFlag(Flags.Carry, this.temp & 0xff00)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0)
        this.setFlag(Flags.Overflow, (this.temp ^ this.accumulator) & (this.temp ^ value) & 0x0080)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        this.accumulator = this.temp & 0x00ff
        return 1
    }

    INC = () => {
        this.fetch()
        this.temp = u8(this.fetched + 1)
        this.write(this.addressAbs, this.temp & 0x00ff)
        this.setFlag(Flags.Zero, (this.temp & 0x00ff) === 0x0000)
        this.setFlag(Flags.Negative, this.temp & 0x0080)
        return 0
    }

    INX = () => {
        this.xRegister = u8(this.xRegister + 1)
        this.setFlag(Flags.Zero, this.xRegister === 0x00)
        this.setFlag(Flags.Negative, this.xRegister & 0x80)
        return 0
    }

    BEQ = () => {
        if (!this.getFlag(Flags.Zero)) return 0

        this.cycles++
        this.addressAbs = u16(this.programCounter + this.addressRel)

        if ((this.addressAbs & 0xff00) !== (this.programCounter & 0xff00)) this.cycles++

        this.programCounter = this.addressAbs

        return 0
    }

    SED = () => {
        this.setFlag(Flags.DecimalMode, true)
        return 0
    }

    // Helpers
    get complete() {
        return this.cycles === 0
    }

    disassemble(nStart: number, nStop: number) {
        const mapLines = new Map<number, string>()
        let addr = nStart

        // Helper function to convert number to hex string
        const hex = (n: number, d: number): string => {
            return n.toString(16).toUpperCase().padStart(d, "0")
        }

        // Starting at the specified address we read an instruction
        // byte, which in turn yields information from the lookup table
        // as to how many additional bytes we need to read and what the
        // addressing mode is. I need this info to assemble human readable
        // syntax, which is different depending upon the addressing mode
        while (addr <= nStop) {
            const lineAddr = addr

            // Prefix line with instruction address
            let sInst = "$" + hex(addr, 4) + ": "

            // Read instruction, and get its readable name
            const opcode = this.read(addr)
            addr++
            sInst += this.lookup[opcode]!.name + " "

            // Get operands from desired locations, and form the
            // instruction based upon its addressing mode. These
            // routines mimic the actual fetch routine of the
            // 6502 in order to get accurate data as part of the
            // instruction
            const addrMode = this.lookup[opcode]!.addressMode

            if (addrMode === this.IMP) {
                sInst += " {IMP}"
            } else if (addrMode === this.IMM) {
                const value = this.read(addr)
                addr++
                sInst += "#$" + hex(value, 2) + " {IMM}"
            } else if (addrMode === this.ZP0) {
                const lo = this.read(addr)
                addr++
                sInst += "$" + hex(lo, 2) + " {ZP0}"
            } else if (addrMode === this.ZPX) {
                const lo = this.read(addr)
                addr++
                sInst += "$" + hex(lo, 2) + ", X {ZPX}"
            } else if (addrMode === this.ZPY) {
                const lo = this.read(addr)
                addr++
                sInst += "$" + hex(lo, 2) + ", Y {ZPY}"
            } else if (addrMode === this.IZX) {
                const lo = this.read(addr)
                addr++
                sInst += "($" + hex(lo, 2) + ", X) {IZX}"
            } else if (addrMode === this.IZY) {
                const lo = this.read(addr)
                addr++
                sInst += "($" + hex(lo, 2) + "), Y {IZY}"
            } else if (addrMode === this.ABS) {
                const lo = this.read(addr)
                addr++
                const hi = this.read(addr)
                addr++
                sInst += "$" + hex((hi << 8) | lo, 4) + " {ABS}"
            } else if (addrMode === this.ABX) {
                const lo = this.read(addr)
                addr++
                const hi = this.read(addr)
                addr++
                sInst += "$" + hex((hi << 8) | lo, 4) + ", X {ABX}"
            } else if (addrMode === this.ABY) {
                const lo = this.read(addr)
                addr++
                const hi = this.read(addr)
                addr++
                sInst += "$" + hex((hi << 8) | lo, 4) + ", Y {ABY}"
            } else if (addrMode === this.IND) {
                const lo = this.read(addr)
                addr++
                const hi = this.read(addr)
                addr++
                sInst += "($" + hex((hi << 8) | lo, 4) + ") {IND}"
            } else if (addrMode === this.REL) {
                const value = this.read(addr)
                addr++
                // Convert to signed 8-bit value
                const offset = value & 0x80 ? value - 256 : value
                sInst += "$" + hex(value, 2) + " [$" + hex(addr + offset, 4) + "] {REL}"
            }

            // Add the formed string to a Map, using the instruction's
            // address as the key. This makes it convenient to look for later
            // as the instructions are variable in length, so a straight up
            // incremental index is not sufficient.
            mapLines.set(lineAddr, sInst)
        }

        return mapLines
    }

    private getLookup() {
        const lookup: Instruction[] = []

        lookup[0x00] = { name: "BRK", operate: this.BRK, addressMode: this.IMP, cycles: 7 }
        lookup[0x01] = { name: "ORA", operate: this.ORA, addressMode: this.IZX, cycles: 6 }
        lookup[0x02] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x03] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x04] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 3 }
        lookup[0x05] = { name: "ORA", operate: this.ORA, addressMode: this.ZP0, cycles: 3 }
        lookup[0x06] = { name: "ASL", operate: this.ASL, addressMode: this.ZP0, cycles: 5 }
        lookup[0x07] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x08] = { name: "PHP", operate: this.PHP, addressMode: this.IMP, cycles: 3 }
        lookup[0x09] = { name: "ORA", operate: this.ORA, addressMode: this.IMM, cycles: 2 }
        lookup[0x0a] = { name: "ASL", operate: this.ASL, addressMode: this.IMP, cycles: 2 }
        lookup[0x0b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x0c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x0d] = { name: "ORA", operate: this.ORA, addressMode: this.ABS, cycles: 4 }
        lookup[0x0e] = { name: "ASL", operate: this.ASL, addressMode: this.ABS, cycles: 6 }
        lookup[0x0f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x10] = { name: "BPL", operate: this.BPL, addressMode: this.REL, cycles: 2 }
        lookup[0x11] = { name: "ORA", operate: this.ORA, addressMode: this.IZY, cycles: 5 }
        lookup[0x12] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x13] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x14] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x15] = { name: "ORA", operate: this.ORA, addressMode: this.ZPX, cycles: 4 }
        lookup[0x16] = { name: "ASL", operate: this.ASL, addressMode: this.ZPX, cycles: 6 }
        lookup[0x17] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x18] = { name: "CLC", operate: this.CLC, addressMode: this.IMP, cycles: 2 }
        lookup[0x19] = { name: "ORA", operate: this.ORA, addressMode: this.ABY, cycles: 4 }
        lookup[0x1a] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x1b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x1c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x1d] = { name: "ORA", operate: this.ORA, addressMode: this.ABX, cycles: 4 }
        lookup[0x1e] = { name: "ASL", operate: this.ASL, addressMode: this.ABX, cycles: 7 }
        lookup[0x1f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x20] = { name: "JSR", operate: this.JSR, addressMode: this.ABS, cycles: 6 }
        lookup[0x21] = { name: "AND", operate: this.AND, addressMode: this.IZX, cycles: 6 }
        lookup[0x22] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x23] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x24] = { name: "BIT", operate: this.BIT, addressMode: this.ZP0, cycles: 3 }
        lookup[0x25] = { name: "AND", operate: this.AND, addressMode: this.ZP0, cycles: 3 }
        lookup[0x26] = { name: "ROL", operate: this.ROL, addressMode: this.ZP0, cycles: 5 }
        lookup[0x27] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x28] = { name: "PLP", operate: this.PLP, addressMode: this.IMP, cycles: 4 }
        lookup[0x29] = { name: "AND", operate: this.AND, addressMode: this.IMM, cycles: 2 }
        lookup[0x2a] = { name: "ROL", operate: this.ROL, addressMode: this.IMP, cycles: 2 }
        lookup[0x2b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x2c] = { name: "BIT", operate: this.BIT, addressMode: this.ABS, cycles: 4 }
        lookup[0x2d] = { name: "AND", operate: this.AND, addressMode: this.ABS, cycles: 4 }
        lookup[0x2e] = { name: "ROL", operate: this.ROL, addressMode: this.ABS, cycles: 6 }
        lookup[0x2f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x30] = { name: "BMI", operate: this.BMI, addressMode: this.REL, cycles: 2 }
        lookup[0x31] = { name: "AND", operate: this.AND, addressMode: this.IZY, cycles: 5 }
        lookup[0x32] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x33] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x34] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x35] = { name: "AND", operate: this.AND, addressMode: this.ZPX, cycles: 4 }
        lookup[0x36] = { name: "ROL", operate: this.ROL, addressMode: this.ZPX, cycles: 6 }
        lookup[0x37] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x38] = { name: "SEC", operate: this.SEC, addressMode: this.IMP, cycles: 2 }
        lookup[0x39] = { name: "AND", operate: this.AND, addressMode: this.ABY, cycles: 4 }
        lookup[0x3a] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x3b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x3c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x3d] = { name: "AND", operate: this.AND, addressMode: this.ABX, cycles: 4 }
        lookup[0x3e] = { name: "ROL", operate: this.ROL, addressMode: this.ABX, cycles: 7 }
        lookup[0x3f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x40] = { name: "RTI", operate: this.RTI, addressMode: this.IMP, cycles: 6 }
        lookup[0x41] = { name: "EOR", operate: this.EOR, addressMode: this.IZX, cycles: 6 }
        lookup[0x42] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x43] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x44] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 3 }
        lookup[0x45] = { name: "EOR", operate: this.EOR, addressMode: this.ZP0, cycles: 3 }
        lookup[0x46] = { name: "LSR", operate: this.LSR, addressMode: this.ZP0, cycles: 5 }
        lookup[0x47] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x48] = { name: "PHA", operate: this.PHA, addressMode: this.IMP, cycles: 3 }
        lookup[0x49] = { name: "EOR", operate: this.EOR, addressMode: this.IMM, cycles: 2 }
        lookup[0x4a] = { name: "LSR", operate: this.LSR, addressMode: this.IMP, cycles: 2 }
        lookup[0x4b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x4c] = { name: "JMP", operate: this.JMP, addressMode: this.ABS, cycles: 3 }
        lookup[0x4d] = { name: "EOR", operate: this.EOR, addressMode: this.ABS, cycles: 4 }
        lookup[0x4e] = { name: "LSR", operate: this.LSR, addressMode: this.ABS, cycles: 6 }
        lookup[0x4f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x50] = { name: "BVC", operate: this.BVC, addressMode: this.REL, cycles: 2 }
        lookup[0x51] = { name: "EOR", operate: this.EOR, addressMode: this.IZY, cycles: 5 }
        lookup[0x52] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x53] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x54] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x55] = { name: "EOR", operate: this.EOR, addressMode: this.ZPX, cycles: 4 }
        lookup[0x56] = { name: "LSR", operate: this.LSR, addressMode: this.ZPX, cycles: 6 }
        lookup[0x57] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x58] = { name: "CLI", operate: this.CLI, addressMode: this.IMP, cycles: 2 }
        lookup[0x59] = { name: "EOR", operate: this.EOR, addressMode: this.ABY, cycles: 4 }
        lookup[0x5a] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x5b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x5c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x5d] = { name: "EOR", operate: this.EOR, addressMode: this.ABX, cycles: 4 }
        lookup[0x5e] = { name: "LSR", operate: this.LSR, addressMode: this.ABX, cycles: 7 }
        lookup[0x5f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x60] = { name: "RTS", operate: this.RTS, addressMode: this.IMP, cycles: 6 }
        lookup[0x61] = { name: "ADC", operate: this.ADC, addressMode: this.IZX, cycles: 6 }
        lookup[0x62] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x63] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x64] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 3 }
        lookup[0x65] = { name: "ADC", operate: this.ADC, addressMode: this.ZP0, cycles: 3 }
        lookup[0x66] = { name: "ROR", operate: this.ROR, addressMode: this.ZP0, cycles: 5 }
        lookup[0x67] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x68] = { name: "PLA", operate: this.PLA, addressMode: this.IMP, cycles: 4 }
        lookup[0x69] = { name: "ADC", operate: this.ADC, addressMode: this.IMM, cycles: 2 }
        lookup[0x6a] = { name: "ROR", operate: this.ROR, addressMode: this.IMP, cycles: 2 }
        lookup[0x6b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x6c] = { name: "JMP", operate: this.JMP, addressMode: this.IND, cycles: 5 }
        lookup[0x6d] = { name: "ADC", operate: this.ADC, addressMode: this.ABS, cycles: 4 }
        lookup[0x6e] = { name: "ROR", operate: this.ROR, addressMode: this.ABS, cycles: 6 }
        lookup[0x6f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x70] = { name: "BVS", operate: this.BVS, addressMode: this.REL, cycles: 2 }
        lookup[0x71] = { name: "ADC", operate: this.ADC, addressMode: this.IZY, cycles: 5 }
        lookup[0x72] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x73] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0x74] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x75] = { name: "ADC", operate: this.ADC, addressMode: this.ZPX, cycles: 4 }
        lookup[0x76] = { name: "ROR", operate: this.ROR, addressMode: this.ZPX, cycles: 6 }
        lookup[0x77] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x78] = { name: "SEI", operate: this.SEI, addressMode: this.IMP, cycles: 2 }
        lookup[0x79] = { name: "ADC", operate: this.ADC, addressMode: this.ABY, cycles: 4 }
        lookup[0x7a] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x7b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x7c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0x7d] = { name: "ADC", operate: this.ADC, addressMode: this.ABX, cycles: 4 }
        lookup[0x7e] = { name: "ROR", operate: this.ROR, addressMode: this.ABX, cycles: 7 }
        lookup[0x7f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0x80] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x81] = { name: "STA", operate: this.STA, addressMode: this.IZX, cycles: 6 }
        lookup[0x82] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x83] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x84] = { name: "STY", operate: this.STY, addressMode: this.ZP0, cycles: 3 }
        lookup[0x85] = { name: "STA", operate: this.STA, addressMode: this.ZP0, cycles: 3 }
        lookup[0x86] = { name: "STX", operate: this.STX, addressMode: this.ZP0, cycles: 3 }
        lookup[0x87] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 3 }
        lookup[0x88] = { name: "DEY", operate: this.DEY, addressMode: this.IMP, cycles: 2 }
        lookup[0x89] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0x8a] = { name: "TXA", operate: this.TXA, addressMode: this.IMP, cycles: 2 }
        lookup[0x8b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x8c] = { name: "STY", operate: this.STY, addressMode: this.ABS, cycles: 4 }
        lookup[0x8d] = { name: "STA", operate: this.STA, addressMode: this.ABS, cycles: 4 }
        lookup[0x8e] = { name: "STX", operate: this.STX, addressMode: this.ABS, cycles: 4 }
        lookup[0x8f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0x90] = { name: "BCC", operate: this.BCC, addressMode: this.REL, cycles: 2 }
        lookup[0x91] = { name: "STA", operate: this.STA, addressMode: this.IZY, cycles: 6 }
        lookup[0x92] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0x93] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0x94] = { name: "STY", operate: this.STY, addressMode: this.ZPX, cycles: 4 }
        lookup[0x95] = { name: "STA", operate: this.STA, addressMode: this.ZPX, cycles: 4 }
        lookup[0x96] = { name: "STX", operate: this.STX, addressMode: this.ZPY, cycles: 4 }
        lookup[0x97] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0x98] = { name: "TYA", operate: this.TYA, addressMode: this.IMP, cycles: 2 }
        lookup[0x99] = { name: "STA", operate: this.STA, addressMode: this.ABY, cycles: 5 }
        lookup[0x9a] = { name: "TXS", operate: this.TXS, addressMode: this.IMP, cycles: 2 }
        lookup[0x9b] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x9c] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 5 }
        lookup[0x9d] = { name: "STA", operate: this.STA, addressMode: this.ABX, cycles: 5 }
        lookup[0x9e] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0x9f] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0xa0] = { name: "LDY", operate: this.LDY, addressMode: this.IMM, cycles: 2 }
        lookup[0xa1] = { name: "LDA", operate: this.LDA, addressMode: this.IZX, cycles: 6 }
        lookup[0xa2] = { name: "LDX", operate: this.LDX, addressMode: this.IMM, cycles: 2 }
        lookup[0xa3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0xa4] = { name: "LDY", operate: this.LDY, addressMode: this.ZP0, cycles: 3 }
        lookup[0xa5] = { name: "LDA", operate: this.LDA, addressMode: this.ZP0, cycles: 3 }
        lookup[0xa6] = { name: "LDX", operate: this.LDX, addressMode: this.ZP0, cycles: 3 }
        lookup[0xa7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 3 }
        lookup[0xa8] = { name: "TAY", operate: this.TAY, addressMode: this.IMP, cycles: 2 }
        lookup[0xa9] = { name: "LDA", operate: this.LDA, addressMode: this.IMM, cycles: 2 }
        lookup[0xaa] = { name: "TAX", operate: this.TAX, addressMode: this.IMP, cycles: 2 }
        lookup[0xab] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0xac] = { name: "LDY", operate: this.LDY, addressMode: this.ABS, cycles: 4 }
        lookup[0xad] = { name: "LDA", operate: this.LDA, addressMode: this.ABS, cycles: 4 }
        lookup[0xae] = { name: "LDX", operate: this.LDX, addressMode: this.ABS, cycles: 4 }
        lookup[0xaf] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0xb0] = { name: "BCS", operate: this.BCS, addressMode: this.REL, cycles: 2 }
        lookup[0xb1] = { name: "LDA", operate: this.LDA, addressMode: this.IZY, cycles: 5 }
        lookup[0xb2] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0xb3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0xb4] = { name: "LDY", operate: this.LDY, addressMode: this.ZPX, cycles: 4 }
        lookup[0xb5] = { name: "LDA", operate: this.LDA, addressMode: this.ZPX, cycles: 4 }
        lookup[0xb6] = { name: "LDX", operate: this.LDX, addressMode: this.ZPY, cycles: 4 }
        lookup[0xb7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0xb8] = { name: "CLV", operate: this.CLV, addressMode: this.IMP, cycles: 2 }
        lookup[0xb9] = { name: "LDA", operate: this.LDA, addressMode: this.ABY, cycles: 4 }
        lookup[0xba] = { name: "TSX", operate: this.TSX, addressMode: this.IMP, cycles: 2 }
        lookup[0xbb] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0xbc] = { name: "LDY", operate: this.LDY, addressMode: this.ABX, cycles: 4 }
        lookup[0xbd] = { name: "LDA", operate: this.LDA, addressMode: this.ABX, cycles: 4 }
        lookup[0xbe] = { name: "LDX", operate: this.LDX, addressMode: this.ABY, cycles: 4 }
        lookup[0xbf] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 4 }
        lookup[0xc0] = { name: "CPY", operate: this.CPY, addressMode: this.IMM, cycles: 2 }
        lookup[0xc1] = { name: "CMP", operate: this.CMP, addressMode: this.IZX, cycles: 6 }
        lookup[0xc2] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0xc3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0xc4] = { name: "CPY", operate: this.CPY, addressMode: this.ZP0, cycles: 3 }
        lookup[0xc5] = { name: "CMP", operate: this.CMP, addressMode: this.ZP0, cycles: 3 }
        lookup[0xc6] = { name: "DEC", operate: this.DEC, addressMode: this.ZP0, cycles: 5 }
        lookup[0xc7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0xc8] = { name: "INY", operate: this.INY, addressMode: this.IMP, cycles: 2 }
        lookup[0xc9] = { name: "CMP", operate: this.CMP, addressMode: this.IMM, cycles: 2 }
        lookup[0xca] = { name: "DEX", operate: this.DEX, addressMode: this.IMP, cycles: 2 }
        lookup[0xcb] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0xcc] = { name: "CPY", operate: this.CPY, addressMode: this.ABS, cycles: 4 }
        lookup[0xcd] = { name: "CMP", operate: this.CMP, addressMode: this.ABS, cycles: 4 }
        lookup[0xce] = { name: "DEC", operate: this.DEC, addressMode: this.ABS, cycles: 6 }
        lookup[0xcf] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0xd0] = { name: "BNE", operate: this.BNE, addressMode: this.REL, cycles: 2 }
        lookup[0xd1] = { name: "CMP", operate: this.CMP, addressMode: this.IZY, cycles: 5 }
        lookup[0xd2] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0xd3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0xd4] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0xd5] = { name: "CMP", operate: this.CMP, addressMode: this.ZPX, cycles: 4 }
        lookup[0xd6] = { name: "DEC", operate: this.DEC, addressMode: this.ZPX, cycles: 6 }
        lookup[0xd7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0xd8] = { name: "CLD", operate: this.CLD, addressMode: this.IMP, cycles: 2 }
        lookup[0xd9] = { name: "CMP", operate: this.CMP, addressMode: this.ABY, cycles: 4 }
        lookup[0xda] = { name: "NOP", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0xdb] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0xdc] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0xdd] = { name: "CMP", operate: this.CMP, addressMode: this.ABX, cycles: 4 }
        lookup[0xde] = { name: "DEC", operate: this.DEC, addressMode: this.ABX, cycles: 7 }
        lookup[0xdf] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0xe0] = { name: "CPX", operate: this.CPX, addressMode: this.IMM, cycles: 2 }
        lookup[0xe1] = { name: "SBC", operate: this.SBC, addressMode: this.IZX, cycles: 6 }
        lookup[0xe2] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0xe3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0xe4] = { name: "CPX", operate: this.CPX, addressMode: this.ZP0, cycles: 3 }
        lookup[0xe5] = { name: "SBC", operate: this.SBC, addressMode: this.ZP0, cycles: 3 }
        lookup[0xe6] = { name: "INC", operate: this.INC, addressMode: this.ZP0, cycles: 5 }
        lookup[0xe7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 5 }
        lookup[0xe8] = { name: "INX", operate: this.INX, addressMode: this.IMP, cycles: 2 }
        lookup[0xe9] = { name: "SBC", operate: this.SBC, addressMode: this.IMM, cycles: 2 }
        lookup[0xea] = { name: "NOP", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0xeb] = { name: "???", operate: this.SBC, addressMode: this.IMP, cycles: 2 }
        lookup[0xec] = { name: "CPX", operate: this.CPX, addressMode: this.ABS, cycles: 4 }
        lookup[0xed] = { name: "SBC", operate: this.SBC, addressMode: this.ABS, cycles: 4 }
        lookup[0xee] = { name: "INC", operate: this.INC, addressMode: this.ABS, cycles: 6 }
        lookup[0xef] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0xf0] = { name: "BEQ", operate: this.BEQ, addressMode: this.REL, cycles: 2 }
        lookup[0xf1] = { name: "SBC", operate: this.SBC, addressMode: this.IZY, cycles: 5 }
        lookup[0xf2] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 2 }
        lookup[0xf3] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 8 }
        lookup[0xf4] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0xf5] = { name: "SBC", operate: this.SBC, addressMode: this.ZPX, cycles: 4 }
        lookup[0xf6] = { name: "INC", operate: this.INC, addressMode: this.ZPX, cycles: 6 }
        lookup[0xf7] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 6 }
        lookup[0xf8] = { name: "SED", operate: this.SED, addressMode: this.IMP, cycles: 2 }
        lookup[0xf9] = { name: "SBC", operate: this.SBC, addressMode: this.ABY, cycles: 4 }
        lookup[0xfa] = { name: "NOP", operate: this.NOP, addressMode: this.IMP, cycles: 2 }
        lookup[0xfb] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }
        lookup[0xfc] = { name: "???", operate: this.NOP, addressMode: this.IMP, cycles: 4 }
        lookup[0xfd] = { name: "SBC", operate: this.SBC, addressMode: this.ABX, cycles: 4 }
        lookup[0xfe] = { name: "INC", operate: this.INC, addressMode: this.ABX, cycles: 7 }
        lookup[0xff] = { name: "???", operate: this.XXX, addressMode: this.IMP, cycles: 7 }

        return lookup
    }
}

interface Instruction {
    name: string
    operate: () => number
    addressMode: () => number
    cycles: number
}
